const BOUND_ATTR = 'data-nais-menu-bound';
const FLYOUT_GAP_PX = 6;
const FLYOUT_HIDE_DELAY_MS = 150;
const FLYOUT_OPEN_CLASS = 'nais-menu__item--flyout-open';

export function initMenu(): void {
    const root = document.querySelector('.nais-sidebar__nav');
    const menu = root?.querySelector('.nais-menu--root');
    if (!(menu instanceof HTMLElement)) return;

    // Safe to re-run after AJAX (does not stack listeners).
    syncMenuOpenState(menu);
    syncCollapsedFlyouts(menu);

    if (menu.hasAttribute(BOUND_ATTR)) {
        return;
    }
    menu.setAttribute(BOUND_ATTR, '1');

    menu.addEventListener('click', (e) => {
        const target = e.target;
        if (!(target instanceof Element)) return;

        const link = target.closest<HTMLAnchorElement>('.nais-menu__link[data-nais-menu-toggle]');
        if (!link || !menu.contains(link)) return;

        const item = link.closest('.nais-menu__item');
        if (!(item instanceof HTMLElement)) return;

        const collapsed = isSidebarCollapsed();
        const isRootItem = item.parentElement === menu;

        // Root modules in collapsed mode open via hover flyout — do not accordion-toggle them.
        if (collapsed && isRootItem) {
            e.preventDefault();
            return;
        }

        e.preventDefault();

        const submenu = item.querySelector(':scope > .nais-menu__flyout > .nais-menu--sub');
        if (!submenu) return;

        const willOpen = submenu.hasAttribute('hidden');
        const parentList = item.parentElement;

        // Accordion only among siblings at the same nesting level.
        if (parentList) {
            parentList.querySelectorAll(':scope > .nais-menu__item').forEach((sibling) => {
                if (sibling === item) return;
                const siblingSub = sibling.querySelector(':scope > .nais-menu__flyout > .nais-menu--sub');
                if (!siblingSub) return;
                siblingSub.setAttribute('hidden', '');
                sibling.classList.remove('always-open');
            });
        }

        if (willOpen) {
            submenu.removeAttribute('hidden');
            item.classList.add('always-open');
        } else {
            submenu.setAttribute('hidden', '');
            item.classList.remove('always-open');
            submenu.querySelectorAll('.nais-menu--sub').forEach((ul) => {
                ul.setAttribute('hidden', '');
                ul.closest('.nais-menu__item')?.classList.remove('always-open');
            });
        }

        // Flyout height changes when nested sections toggle — keep it in the viewport.
        if (collapsed) {
            const openRoot = menu.querySelector<HTMLElement>(`:scope > .${FLYOUT_OPEN_CLASS}`);
            if (openRoot) {
                window.requestAnimationFrame(() => placeCollapsedFlyout(openRoot));
            }
        }
    });

    initCollapsedMenuFlyouts(menu);
    bindSidebarSearch(menu);

    document.querySelectorAll('[data-nais-sidebar-toggle]').forEach((toggle) => {
        if (toggle.hasAttribute('data-nais-menu-sync-bound')) return;
        toggle.setAttribute('data-nais-menu-sync-bound', '1');
        toggle.addEventListener('click', () => {
            window.requestAnimationFrame(() => syncCollapsedFlyouts(menu));
        });
    });
}

function syncMenuOpenState(menu: HTMLElement): void {
    menu.querySelectorAll(':scope > .nais-menu__item.always-open > .nais-menu__flyout > .nais-menu--sub').forEach((ul) => {
        ul.removeAttribute('hidden');
    });

    menu.querySelectorAll(':scope > .nais-menu__item').forEach((item) => {
        const submenu = item.querySelector(':scope > .nais-menu__flyout > .nais-menu--sub');
        if (!submenu) return;

        const hasActiveChild = submenu.querySelector(
            '.nais-menu__item.current-page, .nais-menu__item.active > .nais-menu__link[href]',
        );
        if (!hasActiveChild) return;

        submenu.removeAttribute('hidden');
        item.classList.add('always-open', 'active');
    });

    menu.querySelectorAll<HTMLAnchorElement>('.nais-menu--sub a[href]').forEach((link) => {
        if (link.href !== window.location.href) return;

        link.closest('.nais-menu__item')?.classList.add('current-page', 'active');
        let parent = link.closest('.nais-menu--sub');
        while (parent && parent !== menu) {
            parent.removeAttribute('hidden');
            parent.closest('.nais-menu__item')?.classList.add('always-open', 'active');
            parent = parent.parentElement?.closest('.nais-menu--sub') ?? null;
        }
    });
}

function bindSidebarSearch(menu: HTMLElement): void {
    const search = document.getElementById('nais-sidebar-search') as HTMLInputElement | null;
    if (!search || search.hasAttribute('data-nais-menu-search-bound')) return;
    search.setAttribute('data-nais-menu-search-bound', '1');

    search.addEventListener('input', () => {
        const q = search.value.trim().toLowerCase();
        menu.querySelectorAll<HTMLElement>(':scope > .nais-menu__item').forEach((item) => {
            const text = item.textContent?.toLowerCase() ?? '';
            const match = !q || text.includes(q);
            item.style.display = match ? '' : 'none';
            if (match && q) {
                item.querySelectorAll('.nais-menu--sub').forEach((ul) => ul.removeAttribute('hidden'));
            }
        });
    });
}

function isSidebarCollapsed(): boolean {
    return document.body.classList.contains('nais-sidebar-collapsed');
}

function placeCollapsedFlyout(item: HTMLElement): void {
    if (!isSidebarCollapsed()) return;

    const flyout = item.querySelector<HTMLElement>(':scope > .nais-menu__flyout');
    const link = item.querySelector<HTMLElement>(':scope > .nais-menu__link');
    if (!flyout || !link) return;

    const rect = link.getBoundingClientRect();
    flyout.style.left = `${rect.right + FLYOUT_GAP_PX}px`;
    flyout.style.top = `${rect.top}px`;

    const flyoutRect = flyout.getBoundingClientRect();
    if (flyoutRect.bottom > window.innerHeight - 8) {
        flyout.style.top = `${Math.max(8, window.innerHeight - flyoutRect.height - 8)}px`;
    }

    const bridgeWidth = Math.max(FLYOUT_GAP_PX, flyout.getBoundingClientRect().left - rect.right);
    flyout.style.setProperty('--nais-flyout-bridge-width', `${bridgeWidth}px`);
}

function closeAllCollapsedFlyouts(menu: Element): void {
    menu.querySelectorAll<HTMLElement>(`:scope > .${FLYOUT_OPEN_CLASS}`).forEach((item) => {
        item.classList.remove(FLYOUT_OPEN_CLASS);
    });
}

function initCollapsedMenuFlyouts(menu: HTMLElement): void {
    const rootItems = menu.querySelectorAll<HTMLElement>(':scope > .nais-menu__item');
    let hideTimer: ReturnType<typeof setTimeout> | null = null;

    const cancelScheduledClose = (): void => {
        if (hideTimer !== null) {
            clearTimeout(hideTimer);
            hideTimer = null;
        }
    };

    const openFlyout = (item: HTMLElement): void => {
        if (!isSidebarCollapsed()) return;

        const flyout = item.querySelector<HTMLElement>(':scope > .nais-menu__flyout');
        if (!flyout) return;

        cancelScheduledClose();
        closeAllCollapsedFlyouts(menu);
        item.classList.add(FLYOUT_OPEN_CLASS);
        placeCollapsedFlyout(item);
    };

    const scheduleClose = (item: HTMLElement): void => {
        cancelScheduledClose();
        hideTimer = setTimeout(() => {
            item.classList.remove(FLYOUT_OPEN_CLASS);
            hideTimer = null;
        }, FLYOUT_HIDE_DELAY_MS);
    };

    const isMovingTo = (from: HTMLElement, target: EventTarget | null, container: HTMLElement): boolean => {
        return target instanceof Node && container.contains(target) && target !== from;
    };

    rootItems.forEach((item) => {
        const flyout = item.querySelector<HTMLElement>(':scope > .nais-menu__flyout');
        if (!flyout) return;

        item.addEventListener('mouseenter', () => openFlyout(item));
        item.addEventListener('mouseleave', (e) => {
            if (isMovingTo(item, e.relatedTarget, flyout)) return;
            scheduleClose(item);
        });

        flyout.addEventListener('mouseenter', () => openFlyout(item));
        flyout.addEventListener('mouseleave', (e) => {
            if (isMovingTo(flyout, e.relatedTarget, item)) return;
            scheduleClose(item);
        });

        item.addEventListener('focusin', () => openFlyout(item));
    });

    if (!menu.hasAttribute('data-nais-menu-resize-bound')) {
        menu.setAttribute('data-nais-menu-resize-bound', '1');
        window.addEventListener('resize', () => {
            const open = menu.querySelector<HTMLElement>(`:scope > .${FLYOUT_OPEN_CLASS}`);
            if (open) placeCollapsedFlyout(open);
        });
    }

    document.querySelectorAll('[data-nais-sidebar-toggle]').forEach((toggle) => {
        if (toggle.hasAttribute('data-nais-menu-flyout-bound')) return;
        toggle.setAttribute('data-nais-menu-flyout-bound', '1');
        toggle.addEventListener('click', () => {
            window.requestAnimationFrame(() => {
                cancelScheduledClose();
                closeAllCollapsedFlyouts(menu);
            });
        });
    });
}

function syncCollapsedFlyouts(menu: Element): void {
    const collapsed = document.body.classList.contains('nais-sidebar-collapsed');
    menu.querySelectorAll<HTMLElement>(':scope > .nais-menu__item').forEach((item) => {
        const flyout = item.querySelector(':scope > .nais-menu__flyout');
        if (!flyout) return;

        if (collapsed) {
            // Keep the module's first-level list visible in the flyout; nested sections
            // stay accordion-driven (always-open / active path from syncMenuOpenState).
            const submenu = flyout.querySelector(':scope > .nais-menu--sub');
            if (submenu) submenu.removeAttribute('hidden');
            return;
        }

        const submenu = item.querySelector(':scope > .nais-menu__flyout > .nais-menu--sub');
        if (!submenu) return;

        if (!item.classList.contains('always-open')) {
            submenu.setAttribute('hidden', '');
        }
    });
}
